import { IGetImagesResponse, IPostOneImageUploadRequest } from "../../../interfaces/api/IMedia";
import { IGetWithDisabledPaginationQuery, IGetWithNameQuery, IGetWithPaginationQuery, IGetWithUuidQuery, IMessageResponse, IMessageWithUuidResponse } from "../../../interfaces/api/IQueries";
import { IGetDownloadTemplateMailImageResponse, IGetOneTemplateMailResponse, IGetTemplateFileResponse, IGetTemplateMailImageResponse, IGetTemplateMailsResponse, IPostTemplateMailUploadRequest, IPutTemplateMailRequest } from "../../../interfaces/api/ITemplateMails";
import { api } from "../api";

/**
 * it adds the template mails endpoint to the api
 * 
 * @returns TemplateMailRepository
 */
const TemplateMailRepository = api.injectEndpoints({
    endpoints: (build) => ({
        getTemplates: build.query<IGetTemplateMailsResponse|IMessageResponse, IGetWithPaginationQuery|IGetWithDisabledPaginationQuery>({
            query: (queryParameters) => {
                if("disabled_pagination" in queryParameters) {
                    return {url: `/templates?disabled_pagination=true`, method: "GET"};
                }
                
                if("page" in queryParameters && "per_page" in queryParameters) {
                    return {url: `/templates?page=${queryParameters.page}&per_page=${queryParameters.per_page}`, method: "GET"};
                }

                return {url: "/templates", method: "GET"};
            },          
        }),
        getOneTemplate: build.query<IGetOneTemplateMailResponse, IGetWithUuidQuery>({
            query: (arg) => ({url: `/templates/${arg.uuid}`, method: "GET"}),
        }),
        putOneTemplate: build.mutation<IMessageResponse, IPutTemplateMailRequest>({
            query: ({uuid, data}) => ({url: `/templates/${uuid}`, method: "PUT", data}),
        }),
        postTemplateUpload: build.mutation<IMessageWithUuidResponse|IMessageResponse, IPostTemplateMailUploadRequest>({
            query: (data) =>{
                const formData = new FormData();
                formData.append("file", data.file);
                formData.append("description", data.description);
                formData.append("type", data.type);
                formData.append("subject", data.subject);
                data.client_types.forEach((clientType) => {
                    formData.append("client_types[]", clientType);
                });
                formData.append("status", data.status);
                formData.append("date_validity_start", data.date_validity_start || "");
                formData.append("date_validity_end", data.date_validity_end || "");

                return (
                    {
                        url: "/templates/upload", 
                        method: "post",
                        headers: {
                            'Content-Type': 'multipart/form-data;',
                        },
                        data: formData,
                        formData: true,
                    }
                );
            },
        }),
        getTemplateFile: build.query<IGetTemplateFileResponse, IGetWithUuidQuery>({
            query: (data) => ({url: `/templates/download/${data.uuid}`, method: "GET"}),
        }),
        postOneTemplateImageUpload: build.mutation<IMessageResponse, IPostOneImageUploadRequest>({
            query: (data) => {
                const formData = new FormData();
                formData.append("image", data.image);
                formData.append("description", data.description);
                return(
                    {
                        url: "/templates/images/upload", 
                        method: "post",
                        headers: {
                            'Content-Type': 'multipart/form-data;',
                        },
                        data: formData,
                        formData: true,
                    }
                );
            },
        }),
        getTemplateImages: build.query<IGetImagesResponse|IMessageResponse, IGetWithPaginationQuery|IGetWithDisabledPaginationQuery>({
            query: (queryParameters) => {
                if("disabled_pagination" in queryParameters) {
                    return {url: `/templates/images?disabled_pagination=true`, method: "GET"};
                }
                
                if("page" in queryParameters && "per_page" in queryParameters) {
                    return {url: `/templates/images?page=${queryParameters.page}&per_page=${queryParameters.per_page}`, method: "GET"};
                }

                return {url: "/templates/images", method: "GET"};
            }
        }),
        getOneTemplateImage: build.query<IGetTemplateMailImageResponse, IGetWithUuidQuery>({
            query: (uuid) => ({url: `/templates/images/${uuid}`, method: "GET"}),
        }),
        getOneDownloadTemplateImage: build.query<IGetDownloadTemplateMailImageResponse, IGetWithNameQuery>({
            query: (arg) => ({url: `/templates/images/${arg.name}/download`, method: "GET"}),
        }),
    }),
});

export const {
    useGetTemplatesQuery,
    useGetOneTemplateQuery,
    usePutOneTemplateMutation,
    usePostTemplateUploadMutation,
    usePostOneTemplateImageUploadMutation,
    useGetTemplateFileQuery,
    useLazyGetTemplateFileQuery,
    useGetTemplateImagesQuery,
    useGetOneTemplateImageQuery,
    useLazyGetOneDownloadTemplateImageQuery
} = TemplateMailRepository;